'use client';
import React from 'react';
import { Document } from '@contentful/rich-text-types';
import Script from 'next/script';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import { BASE_URL, SITE_NAME } from '@src/typescriptGlobals/constants';

import Dot from '@src/components/Icons/Dot';
import Container from '@src/components/Shared/Container';
import NextImage from '@src/components/Shared/NextImage';
import NextLink from '@src/components/Shared/NextLink';
import ContentfulRenderer from '@src/components/Shared/ContentfulRenderer';

import useInspectorMode from '@src/hooks/useInspectorMode';
import usePreviewContent from '@src/hooks/usePreviewContent';
import LanguageSwitcher from '@src/components/Shared/LanguageSwitcher';

import CategoryPills from '@src/components/Shared/CategoryPills';
import Authors from '@src/components/Shared/Authors/Authors';

import isEmpty from '@src/utils/isEmpty';
import dateFormatter from '@src/utils/dateFormatter';
import constructURL from '@src/utils/constructURL';
import getPlainTextFromRichText from '@src/utils/getPlainTextFromRichText';

import { PostPageClientPropTypes } from './PostPage.types';

const PostPageClient: React.FC<PostPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const post = usePreviewContent({ content: data?.post });

  // Enable inspector mode for the entry
  const inspectorProps = useInspectorMode({ entryId: post?.sys?.id });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(constructURL({ languageCode: data?.locale, slug: `/news/${data?.slug}`, baseURL: '' }));
  setContentType('site-page');

  return (
    <>
      <Container className='mt-[5rem]'>
        <div className='mx-auto max-w-7xl'>
          {post?.title && (
            <h1
              className='text-body-copy--dark 3xl:text-[4.8rem] 3xl:leading-[5.6rem] mb-[2.4rem] text-[2rem] leading-[2.4rem] font-medium text-pretty md:text-[3.2rem] md:leading-[4rem]'
              {...inspectorProps({ fieldId: 'title' })}
            >
              {post?.title}
            </h1>
          )}

          <div
            className='mb-[1.6rem] flex flex-wrap items-center gap-[0.8rem] text-[1.4rem] md:text-[1.6rem]'
            {...inspectorProps({ fieldId: 'categories' })}
          >
            <p>
              {post?.categoriesV2Collection?.items?.map((category, index, array) => (
                <React.Fragment key={category?.sys?.id}>
                  <NextLink
                    locale={data?.locale?.toLowerCase()}
                    className='text-body-copy--dark font-normal'
                    href={`/news?category=${category?.slug}`}
                  >
                    {category?.title}
                  </NextLink>
                  {index < array.length - 1 && ', '}
                </React.Fragment>
              ))}
            </p>
            {!isEmpty({ value: post?.datePublished }) ? (
              <>
                <Dot className='text-body-copy--dark' />
                <time dateTime={post?.datePublished}>{dateFormatter(post.datePublished, 'UTC')}</time>
              </>
            ) : (
              ''
            )}

            {post?.datePublished &&
              post?.sys?.publishedAt &&
              Date.parse(post.sys.publishedAt) > Date.parse(post?.sys?.firstPublishedAt ?? '') &&
              dateFormatter(post.sys.publishedAt, 'Australia/Brisbane') !==
                dateFormatter(post.datePublished, 'UTC') && (
                <span className='capitalize sm:ml-auto'>
                  {data?.globalTextBlock?.lastUpdated}{' '}
                  <time dateTime={post.sys.publishedAt}>
                    {dateFormatter(post.sys.publishedAt, 'Australia/Brisbane')}
                  </time>
                </span>
              )}
          </div>

          <CategoryPills className='mb-[2rem]' post={post} locale={data?.locale?.toLowerCase()} />

          <div className='mb-[3rem] flex items-center' {...inspectorProps({ fieldId: 'languageCode' })}>
            <p>{data?.globalTextBlock?.availableIn}</p>
            <LanguageSwitcher post={post} />
          </div>

          <NextImage
            className='bg-background-primary mb-[2.4rem]'
            priority
            entryId={post?.sys?.id}
            fieldId='featuredImage'
            src={post?.featuredImage?.url}
            alt={post?.featuredImage?.description ?? ''}
            width={post?.featuredImage?.width}
            aspectRatio={post?.featuredImage?.width / post?.featuredImage?.height}
          />

          <Authors authors={post?.authorsCollection?.items} entryId={post?.sys?.id} fieldId='authors' />

          <ContentfulRenderer
            document={post?.body?.json as Document}
            links={post?.body?.links}
            inspectorProps={inspectorProps({ fieldId: 'body' })}
          />
        </div>
      </Container>

      <Script
        id='app-ld-json'
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            (() => {
              const authors = post?.authorsCollection?.items?.filter((author) => Boolean(author?.name)) ?? [];
              const hasAuthors = authors.length > 0;

              const articleSections = Array.from(
                new Set(
                  [
                    ...(post?.categoriesV2Collection?.items?.map((category) => category?.title) ?? []),
                    ...(post?.productsCollection?.items?.map((product) => product?.name) ?? []),
                    ...(post?.topicsCollection?.items?.map((topic) => topic?.title) ?? []),
                  ].filter(Boolean),
                ),
              );

              return {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Article',
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#article`,
                    isPartOf: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}`,
                    },
                    author: hasAuthors
                      ? authors.map((_, index) => ({
                          '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#/schema/person/${index + 1}`,
                        }))
                      : {
                          '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#organization`,
                        },
                    headline: `${post.title}`,
                    datePublished: `${post.datePublished}`,
                    dateModified: `${post?.sys?.publishedAt ?? post?.datePublished}`,
                    mainEntityOfPage: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}`,
                    },
                    wordCount: 1394,
                    publisher: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#organization`,
                    },
                    image: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#primaryimage`,
                    },
                    thumbnailUrl: post.thumbnail?.url,
                    ...(articleSections.length ? { articleSection: articleSections } : {}),
                    inLanguage: post?.languageCode,
                  },
                  {
                    '@type': 'WebPage',
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}`,
                    url: `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}`,
                    name: `${post.title} | ${SITE_NAME}`,
                    isPartOf: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#website`,
                    },
                    primaryImageOfPage: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#primaryimage`,
                    },
                    image: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#primaryimage`,
                    },
                    thumbnailUrl: `${post.thumbnail?.url}`,
                    datePublished: `${post.datePublished}`,
                    dateModified: `${post?.sys?.publishedAt ?? post?.datePublished}`,
                    description: `${post.excerpt}`,
                    inLanguage: post?.languageCode,
                    potentialAction: [
                      {
                        '@type': 'ReadAction',
                        target: [
                          `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}`,
                        ],
                      },
                    ],
                  },
                  {
                    '@type': 'ImageObject',
                    inLanguage: post?.languageCode,
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#primaryimage`,
                    url: `${post.thumbnail?.url}`,
                    contentUrl: `${post.thumbnail?.url}`,
                    width: 1080,
                    height: 877,
                  },
                  {
                    '@type': 'BreadcrumbList',
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/${post.slug}/#breadcrumb`,
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: `${post.title}`,
                      },
                    ],
                  },
                  {
                    '@type': 'WebSite',
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#website`,
                    url: `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}`,
                    name: `${SITE_NAME} | Human Measurement Technologies`,
                    description: 'Move. Measure. Modify',
                    publisher: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#organization`,
                    },
                    inLanguage: post?.languageCode,
                  },
                  {
                    '@type': 'Organization',
                    '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#organization`,
                    name: SITE_NAME,
                    url: `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}`,
                    logo: {
                      '@type': 'ImageObject',
                      inLanguage: post?.languageCode,
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#/schema/logo/image/`,
                      url: '',
                      contentUrl: '',
                      caption: SITE_NAME,
                    },
                    image: {
                      '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#/schema/logo/image/`,
                    },
                  },
                  ...(hasAuthors
                    ? authors.map((author, index) => {
                        const authorUrl = author?.linkedin ?? author?.twitter ?? author?.instagram;
                        const sameAs = [author?.linkedin, author?.twitter, author?.instagram].filter(Boolean);

                        return {
                          '@type': 'Person',
                          '@id': `${BASE_URL}${post?.languageCode?.toLowerCase() === 'en' ? '' : `/${post?.languageCode?.toLowerCase()}`}/#/schema/person/${index + 1}`,
                          name: author.name,
                          ...((description) => (description ? { description } : {}))(
                            getPlainTextFromRichText(author?.bodyCopy?.json)?.trim(),
                          ),
                          ...(author?.qualifications ? { honorificSuffix: author.qualifications } : {}),
                          ...(author?.profile?.url
                            ? {
                                image: {
                                  '@type': 'ImageObject',
                                  inLanguage: post?.languageCode,
                                  url: author.profile.url,
                                  contentUrl: author.profile.url,
                                  caption: author.name,
                                },
                              }
                            : {}),
                          ...(authorUrl ? { url: authorUrl } : {}),
                          ...(sameAs.length ? { sameAs } : {}),
                        };
                      })
                    : []),
                ],
              };
            })(),
            null,
            '\t',
          ),
        }}
      />
    </>
  );
};

export default PostPageClient;
